import React, { useState } from "react";
import styled from "styled-components";

import Avatar from "../avatar/Avatar";
import Badge from "../badge/Badge";
import DateDisplay, { DATE_DISPLAY_TYPE } from "../date-display/DateDisplay";
import { isClientGroupPosition } from "../../utils/memberPositionUtils";
import {
  getChatThreadDisplayName,
  type ChatThread,
} from "../../app/services/chatThreads";

/**
 * オファー非依存のシンプルな DM スレッドの一覧行。
 * 契約スレッド用の `ThreadItem`（status / requester / contractor 前提）とは別物で、
 * ステータス表示を持たず、参加者のアバターを「このプロジェクトの契約」
 * （master_contract）と同じスタイルで並べて表示する。
 */

interface ChatThreadItemProps {
  thread: ChatThread;
  currentUserId?: number;
  isSelected?: boolean;
  onClick: () => void;
}

// アバタースタック表示設定（ThreadItem の master_contract と揃える）
const MAX_VISIBLE_AVATARS = 4;
const STACK_AVATAR_SIZE = 20;
const STACK_OVERLAP_MARGIN = "-6px";
const STACK_GROUP_BOUNDARY_MARGIN = "4px";

const Container = styled.div<{
  $isSelected: boolean;
  $isHover: boolean;
  $hasUnread: boolean;
}>`
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: 8px 4px 8px 8px;
  cursor: ${({ $isSelected }) => ($isSelected ? "default" : "pointer")};
  user-select: none;
  background-color: ${({ $isSelected, $isHover }) =>
    $isSelected
      ? "var(--soremo-border)"
      : $isHover
        ? "var(--soremo-background)"
        : "#ffffff"};
  transition: background-color 0.2s ease;
  border-bottom: 1px solid var(--soremo-border);
  border-left: 6px solid
    ${({ $hasUnread }) =>
      $hasUnread ? "var(--soremo-blue)" : "var(--soremo-border)"};
`;

const AvatarRow = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

const StackedAvatarWrapper = styled.div<{
  $index: number;
  $total: number;
  $isGroupBoundary: boolean;
}>`
  position: relative;
  margin-left: ${({ $isGroupBoundary }) =>
    $isGroupBoundary ? STACK_GROUP_BOUNDARY_MARGIN : STACK_OVERLAP_MARGIN};
  z-index: ${({ $total, $index }) => $total - $index};
  &:first-child {
    margin-left: 0;
  }
`;

const OverflowCounter = styled.span.attrs({ className: "label8" })`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  margin-left: -4px;
  background: #f0f0f0;
  border: 1px solid #fff;
  border-radius: 50%;
  font-size: 8px;
`;

const DisplayName = styled.div.attrs({ className: "heading-spacing-13" })`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Preview = styled.div.attrs({
  className: "bodytext-11 u-line-height-100 u-text-light-gray",
})`
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Icon = styled.span<{ $isHover: boolean; $isSelected: boolean }>`
  font-size: 24px;
  color: ${({ $isSelected, $isHover }) =>
    $isSelected || $isHover
      ? "var(--soremo-light-gray)"
      : "var(--soremo-border)"};
`;

const ChatThreadItem: React.FC<ChatThreadItemProps> = ({
  thread,
  currentUserId,
  isSelected = false,
  onClick,
}) => {
  const [isHover, setIsHover] = useState(false);

  const displayName = getChatThreadDisplayName(thread, currentUserId);
  const hasUnread = thread.unread_count > 0;
  const preview = thread.last_message
    ? `${
        thread.last_message.author?.id === currentUserId ? "あなた: " : ""
      }${thread.last_message.message}`
    : "メッセージがありません";

  // 表示は自分を含む全メンバー。CLIENT グループ → その他（PRODUCER 等）の順に並べ、
  // 境界の前で余白を空ける（契約スレッドの master_contract アバタースタックと同じ視覚）。
  const sortedMembers = [...thread.members].sort((a, b) => {
    const ra = a.position && isClientGroupPosition(a.position) ? 0 : 1;
    const rb = b.position && isClientGroupPosition(b.position) ? 0 : 1;
    return ra - rb;
  });

  const renderAvatarStack = () => {
    const visible = sortedMembers.slice(0, MAX_VISIBLE_AVATARS);
    const remaining = sortedMembers.length - visible.length;
    if (visible.length === 0) return null;
    return (
      <AvatarRow>
        {visible.map((m, i) => {
          const prev = visible[i - 1];
          const isGroupBoundary =
            i > 0 &&
            isClientGroupPosition(prev?.position ?? "") &&
            !isClientGroupPosition(m.position ?? "");
          return (
            <StackedAvatarWrapper
              key={m.id ?? i}
              $index={i}
              $total={visible.length}
              $isGroupBoundary={isGroupBoundary}
            >
              <Avatar
                size={STACK_AVATAR_SIZE}
                src={m.avatar ?? undefined}
                name={m.display_name}
              />
            </StackedAvatarWrapper>
          );
        })}
        {remaining > 0 && <OverflowCounter>+{remaining}</OverflowCounter>}
      </AvatarRow>
    );
  };

  return (
    <Container
      $isSelected={isSelected}
      $isHover={isHover}
      $hasUnread={hasUnread}
      onClick={onClick}
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
    >
      <div className="u-w100 u-row u-gap8">
        <div className="u-w100 u-col u-gap8">
          <div className="u-w100 u-row-between u-gap8">
            <DisplayName>{displayName}</DisplayName>
            <div className="label8 u-text-light-gray">
              <DateDisplay
                date={thread.updated_at}
                type={DATE_DISPLAY_TYPE.ADAPTIVE}
              />
            </div>
          </div>

          {renderAvatarStack()}

          <Preview>{preview}</Preview>
        </div>

        <div className="u-row">
          {hasUnread && <Badge count={thread.unread_count} />}
          <Icon
            className="material-symbols-rounded"
            $isHover={isHover}
            $isSelected={isSelected}
          >
            chevron_right
          </Icon>
        </div>
      </div>
    </Container>
  );
};

export default ChatThreadItem;
